import React, { useState, useEffect } from "react";
import {
    CircularProgress,
    Typography,
    Avatar,
    Box,
    Button,
    TextField,
    Paper,
    Select,
    MenuItem,
} from "@mui/material";
import { styled } from "@mui/system";
import { ImagePlus } from "lucide-react";
import Swal from "sweetalert2";

import Api from "../../utils/Api";
import { useNavigate } from "react-router-dom";

import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, ContentState, Modifier } from "draft-js";
import draftToHtml from "draftjs-to-html";
import DOMPurify from 'dompurify';

import { Select as AntdSelect, message } from 'antd';
import { useCompany } from "../../contexts/CompanyContext";
const { Option } = AntdSelect;

const ProfileContainer = styled(Paper)(({ theme }) => ({
    backgroundColor: "rgb(28, 28, 28, 0.9)",
    color: "#fff",
    maxWidth: 1020,
    margin: "20px auto",
    padding: "40px",
    borderRadius: "10px",
    boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.5)",
    transition: "0.3s",
    "&:hover": {
        boxShadow: "0px 15px 35px rgba(0, 0, 0, 0.7)",
    },
}));

const ProfileHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    marginBottom: "30px",
    position: "relative",
}));

const ProfileDetailsContainer = styled(Box)(({ theme }) => ({
    marginTop: "20px",
    padding: "10px 0",
}));

const ProfileField = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #333",
    padding: "15px 10px",
    borderRadius: '10px',
}));

const Label = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    color: "#0BD349",
    fontSize: "16px",
}));

const SaveButton = styled(Button)(({ theme }) => ({
    backgroundColor: "#0BD349",
    color: "#000",
    marginRight: "10px",
    "&:hover": {
        backgroundColor: "#0a8d2d",
    },
    transition: "0.3s",
}));

const CancelButton = styled(Button)(({ theme }) => ({
    backgroundColor: "#fff",
    color: "#000",
    "&:hover": {
        backgroundColor: "#c1c1c1",
    },
    transition: "0.3s",
}));

const ProfileImageButton = styled(Button)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    backgroundColor: "transparent",
    color: "#0BD349",
    border: "2px dashed #0BD349",
    borderRadius: "5px",
    padding: "10px",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "rgba(11, 211, 73, 0.1)",
    },
}));

function AddCompanyProfile() {
    const { fetchCompanies } = useCompany();

    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [editedDetails, setEditedDetails] = useState({
        active: 1
    });
    const [selectedImage, setSelectedImage] = useState(null);

    const [categories, setCategories] = useState([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);

    const [editorState, setEditorState] = useState(EditorState.createEmpty());
    const navigate = useNavigate();

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setIsLoading(true);
                const response = await Api.get("/listing-categories");
                if (response.data.success) {
                    setCategories(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching categories:", error);
            } finally {
                setIsLoading(false);
            }
        };

        // Fetch categories when component mounts
        fetchCategories();
    }, []);

    const CHARACTER_LIMIT = 2500;

    const toolbarOptions = {
        options: ['inline', 'blockType', 'history'],
        inline: {
            options: ['bold', 'italic', 'underline'],
        },
        blockType: {
            options: ['Normal', 'H4'],
        },
    };

    const editorLabels = {
        'components.controls.blocktype.h4': 'Überschrift',
        'components.controls.blocktype.normal': 'Normal',
    };

    const truncateContent = (content) => {
        return content.slice(0, CHARACTER_LIMIT);
    };

    const onEditorStateChange = (es) => {
        const plainText = es.getCurrentContent().getPlainText();

        if (plainText.length > CHARACTER_LIMIT) {
            const truncatedText = truncateContent(plainText);
            const newContentState = ContentState.createFromText(truncatedText);
            const newEditorState = EditorState.push(es, newContentState, 'change-block-data');

            setEditorState(newEditorState);
            setEditedDetails((prev) => ({
                ...prev,
                about_company: DOMPurify.sanitize(
                    draftToHtml(convertToRaw(newContentState))
                ),
            }));
        } else {
            setEditorState(es);
            setEditedDetails((prev) => ({
                ...prev,
                about_company: DOMPurify.sanitize(
                    draftToHtml(convertToRaw(es.getCurrentContent()))
                ),
            }));
        }
    };

    const handlePastedText = (text, html, editorState, onChange) => {
        try {
            const currentContent = editorState.getCurrentContent();
            const selection = editorState.getSelection();

            // Ensure the new text is inserted at the current cursor position
            const contentStateWithInsertedText = Modifier.insertText(
                currentContent,
                selection,
                text
            );

            // Enforce character limit on the entire text
            const plainText = contentStateWithInsertedText.getPlainText();
            const truncatedText = truncateContent(plainText);

            // Create new content state with the truncated text
            const newContent = ContentState.createFromText(truncatedText);

            // Update the editor state with the new content
            const newState = EditorState.push(editorState, newContent, 'insert-characters');
            onChange(newState);

            return true; // Prevent default paste behavior 
        } catch (error) {
            console.error(error);
        }
    };

    const handleInputChange = (e) => {
        setEditedDetails({
            ...editedDetails,
            [e.target.name]: e.target.value,
        });
    };

    const handleSelectCategories = (value) => {
        setSelectedCategoryIds(value);
        // const { value, checked } = e.target;
    };

    const handleSaveProfile = async () => {
        try {
            if (!editedDetails.email || !editedDetails.password) {
                message.error("E-Mail und Passwort sind erforderlich");
                return;
            }
            const token = JSON.parse(localStorage.getItem("_pildat"))?.token;

            if (!token) {
                setError("Die Autorisierung ist fehlgeschlagen. Bitte melden Sie sich erneut an.");
                return;
            }

            const profileData = { ...editedDetails, listing_category: selectedCategoryIds.join(',') };

            if (selectedImage) {
                profileData.profile_image = selectedImage;
            }

            const response = await Api.post(`addNewUser`, profileData);

            if (response.status >= 200 && response.status < 300) {
                Swal.fire({
                    icon: "success",
                    title: "Profil gespeichert",
                    text: "Das Unternehmensprofil wurde erfolgreich gespeichert!",
                    iconColor: '#000',
                    confirmButtonColor: '#000'
                });
                fetchCompanies();
                navigate('/dashboard/companies');
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Fehler beim gespeichert des Profils",
                    text: response?.data?.message || "Das Profil des Unternehmens konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.",
                    iconColor: '#000',
                    confirmButtonColor: '#000'
                });
            }
        } catch (err) {
            console.error(err);
            Swal.fire({
                icon: "error",
                title: "Fehler beim gespeichert des Profils",
                text: err?.response?.data?.message || err.message,
                iconColor: '#000',
                confirmButtonColor: '#000'
            });
        }
    };

    const handleCancelEdit = () => {
        navigate('/dashboard/companies');
    };

    const compressAndConvertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const reader = new FileReader();

            reader.onload = (event) => {
                img.src = event.target.result;
            };

            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                const maxWidth = 1000;
                const maxHeight = 1000;
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > maxWidth) {
                        height *= maxWidth / width;
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width *= maxHeight / height;
                        height = maxHeight;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);

                const base64String = canvas.toDataURL('image/jpeg', 0.7);

                resolve(base64String);
            };

            img.onerror = (error) => {
                reject(error);
            };

            reader.onerror = (error) => {
                reject(error);
            };

            reader.readAsDataURL(file);
        });
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            try {
                const base64String = await compressAndConvertToBase64(file);
                setSelectedImage(base64String);
            } catch (error) {
                console.error("Error converting image to base64:", error);
            }
        }
    };

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const handleStatusChange = (event) => {
        setEditedDetails({
            ...editedDetails,
            active: event.target.value === 'Active' ? true : false,
        });
    };

    const handleClassificationChange = (event) => {
        setEditedDetails({
            ...editedDetails,
            classification: event.target.value,
        });
    };

    return (
        <div style={{
            position: 'relative'
        }}>
            <img style={{
                width: '100%',
                objectFit: 'contain',
                position: 'fixed',
                zIndex: -1
            }} src="https://cdn.prod.website-files.com/65f03c978efea1c906592c94/65f41d5f2ba41fbbe6c5514c_AdobeStock_546815906_gruen_klein_bearbeitet_002.webp" />
            <ProfileContainer elevation={3}>
                <ProfileHeader>
                    <Avatar
                        alt={"Company Profile"}
                        src={
                            selectedImage
                                ? selectedImage
                                : "/default-avatar.png"
                        }
                        sx={{ width: 100, height: 100, marginRight: "20px" }}
                    />

                    <Box>
                        <Typography variant="body2" style={{ color: "#0BD349" }}>
                            Company Email
                        </Typography>
                        <Typography variant="body2" style={{ color: "#fff" }}>
                            Company Name
                        </Typography>
                    </Box>
                </ProfileHeader>

                {/* Personal Information Section */}
                <ProfileDetailsContainer>
                    <Typography variant="h6" style={{ color: "#0BD349", marginBottom: "10px" }}>
                        {/* Account Data */}
                        Kontodaten
                    </Typography>
                    <ProfileImageButton
                        component="label"
                        aria-label="edit picture"
                    >
                        <ImagePlus color="#0BD349" />
                        <Typography
                            variant="body2"
                            style={{ marginLeft: "10px" }}
                        >
                            {/* Select New Profile Picture */}
                            Laden Sie hier Ihr Logo hoch
                        </Typography>
                        <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={handleImageChange}
                        />
                    </ProfileImageButton>

                    {[
                        { label: "Login-E-Mailadresse:", value: "email" },
                        { label: "Passwort", value: "password" },
                    ].map(({ label, value }) => (
                        <ProfileField key={value}>
                            <Label>{label}:</Label>
                            <div style={{
                                position: 'relative'
                            }}>
                                <TextField
                                    inputProps={{
                                        maxLength: 255,
                                    }}
                                    name={value}
                                    value={editedDetails[value] || ""}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        color: "#fff",
                                        width: '300px',
                                        backgroundColor: "#333",
                                        borderRadius: '8px',
                                        border: "none",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            border: "none",
                                        },
                                    }}
                                />
                            </div>
                        </ProfileField>
                    ))}
                </ProfileDetailsContainer>

                <ProfileDetailsContainer>
                    <Typography variant="h6" style={{ color: "#0BD349", marginBottom: "10px" }}>
                        {/* Personal Information */}
                        Informationen zum Unternehmen
                    </Typography>
                    {[
                        { label: "Zentrale E-Mailadresse", value: "company_email" },
                        { label: "Name der Firma", value: "companyname" },
                        { label: "Vollständige Adresse", value: "address" },
                        { label: "Zentrale Telefonnummer", value: "telephone" },
                        { label: "Halle", value: "hall_no" },
                        { label: "Standplatz", value: "booth_location" },
                        // { label: "Über das Unternehmen", value: "about_company" },
                        { label: "Website", value: "company_website" },
                    ].map(({ label, value }) => (
                        <ProfileField key={value}>
                            <Label>{label}:</Label>
                            <div style={{
                                position: 'relative'
                            }}>
                                <TextField
                                    inputProps={{
                                        maxLength: value === 'hall_no' ? 3 : (value === 'booth_location' ? 4 : 255),
                                    }}
                                    multiline={value === 'about_company' || value === 'address'}
                                    rows={4}
                                    name={value}
                                    value={editedDetails[value] || ""}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        color: "#fff",
                                        width: '300px',
                                        backgroundColor: "#333",
                                        borderRadius: '8px',
                                        border: "none",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            border: "none",
                                        },
                                    }}
                                />
                                {
                                    (value === 'about_company' || value === 'address') && (
                                        <span style={{
                                            position: 'absolute',
                                            right: 5,
                                            bottom: 1,
                                            fontSize: 8,
                                            color: 'gray'
                                        }}>Max 255 Zeichen</span>
                                    )
                                }
                            </div>
                        </ProfileField>
                    ))}
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                        borderBottom: '1px solid #333',
                        padding: '15px 10px',
                        borderRadius: '10px'
                    }}>
                        <Label>Über das Unternehmen:</Label>
                        <div style={{ position: 'relative' }}>
                            <Editor
                                toolbar={toolbarOptions}
                                localization={{ locale: 'en', translations: editorLabels }}
                                editorState={editorState}
                                toolbarClassName="toolbar"
                                wrapperClassName="wrapper"
                                editorClassName="editor"
                                onEditorStateChange={onEditorStateChange}
                                handlePastedText={(text, html) => handlePastedText(text, html, editorState, setEditorState)}
                            />
                            <span
                                style={{
                                    position: 'absolute',
                                    right: 5,
                                    bottom: 1,
                                    fontSize: 8,
                                    color: 'gray',
                                }}
                            >
                                Max. 2500 Zeichen
                            </span>
                        </div>
                    </div>
                </ProfileDetailsContainer>

                <ProfileDetailsContainer>
                    {/* Representative Information Fields */}
                    <Typography variant="h6" style={{ color: "#0BD349", marginTop: "20px" }}>
                        {/* Company Representative Information */}
                        Kontakt zum Ansprechpartner
                    </Typography>
                    {[
                        { label: "Name", value: "representativeName" },
                        { label: "Position", value: "representativePosition" },
                        { label: "E-Mail", value: "representativeEmail" },
                        { label: "Telefonnummer", value: "representativeTelephone" },
                    ].map(({ label, value }) => (
                        <ProfileField key={value}>
                            <Label>{label}:</Label>
                            <TextField
                                inputProps={{
                                    maxLength: 255
                                }}
                                name={value}
                                value={editedDetails[value] || ""}
                                onChange={handleInputChange}
                                variant="outlined"
                                size="small"
                                sx={{
                                    color: "#fff",
                                    width: '300px',
                                    backgroundColor: "#333",
                                    borderRadius: '8px',
                                    border: "none",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        border: "none",
                                    },
                                }}
                            />
                        </ProfileField>
                    ))}
                </ProfileDetailsContainer>

                {/* Social Links Section */}
                <ProfileDetailsContainer>
                    <Typography variant="h6" style={{ color: "#0BD349", marginBottom: "10px" }}>
                        {/* Social Links */}
                        Social-Media Links
                    </Typography>
                    {[
                        { label: "LinkedIn", value: "linkedin" },
                        { label: "Instagram", value: "twitter" },
                        { label: "Facebook", value: "facebook" },
                        { label: "Youtube", value: "youtube" }
                    ].map(({ label, value }) => (
                        <ProfileField key={value}>
                            <Label>{label}:</Label>
                            <TextField
                                inputProps={{
                                    maxLength: 255
                                }}
                                name={value}
                                value={editedDetails[value] || ""}
                                onChange={handleInputChange}
                                variant="outlined"
                                size="small"
                                sx={{
                                    color: "#fff",
                                    width: '300px',
                                    backgroundColor: "#333",
                                    borderRadius: '8px',
                                    border: "none",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        border: "none",
                                    },
                                }}
                            />
                        </ProfileField>
                    ))}
                </ProfileDetailsContainer>

                <ProfileDetailsContainer>
                    <Typography variant="h6" style={{ color: "#0BD349", marginBottom: "10px" }}>
                        {/* Company Status */}
                        Firmenstatus
                    </Typography>
                    <ProfileField>
                        <Label>Status:</Label>
                        <Select
                            value={editedDetails.active ? 'Active' : 'Inactive'}
                            onChange={handleStatusChange}
                            sx={{
                                color: "#fff",
                                width: "300px",
                                backgroundColor: "#333",
                                borderRadius: "8px",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                            }}
                        >
                            <MenuItem value="Active">Aktiv</MenuItem>
                            <MenuItem value="Inactive">Inaktiv</MenuItem>
                        </Select>
                    </ProfileField>
                    <ProfileField>
                        <Label>Klassifizierungen:</Label>
                        <Select
                            value={editedDetails?.classification || "OHNE"}
                            onChange={handleClassificationChange}
                            sx={{
                                color: "#fff",
                                width: "300px",
                                backgroundColor: "#333",
                                borderRadius: "8px",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                },
                            }}
                        >
                            <MenuItem value="HAUPTPARTNER">HAUPTPARTNER</MenuItem>
                            <MenuItem value="PLATINUMPARTNER">PLATINUMPARTNER</MenuItem>
                            <MenuItem value="GOLDPARTNER">GOLDPARTNER</MenuItem>
                            <MenuItem value="SILBERPARTNER">SILBERPARTNER</MenuItem>
                            <MenuItem value="BRONZEPARTNER">BRONZEPARTNER</MenuItem>
                            <MenuItem value="OHNE">OHNE</MenuItem>
                        </Select>
                    </ProfileField>
                </ProfileDetailsContainer>

                <ProfileDetailsContainer>
                    <Typography variant="h6" style={{ color: "#0BD349", marginBottom: "10px" }}>
                        Wählen Sie die Branchen, in denen Ihr Unternehmen tätig ist. (Mehrfachauswahl möglich)
                    </Typography>

                    <AntdSelect
                        className="custom-select"
                        mode="multiple"
                        showSearch
                        style={{ width: '100%' }}
                        placeholder="Select categories"
                        dropdownStyle={{
                            backgroundColor: '#333', // Dark background for dropdown options
                            color: '#fff'            // White text color for dropdown options
                        }}
                        value={selectedCategoryIds}
                        onChange={handleSelectCategories}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {categories.map((category) => (
                            <Option style={{ backgroundColor: '#333', color: '#fff' }} key={category.id} value={category.id.toString()}>
                                {category.category_name}
                            </Option>
                        ))}
                    </AntdSelect>
                </ProfileDetailsContainer>

                <Box sx={{ marginTop: "20px" }}>
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between"
                    }}>
                        <div>
                            <SaveButton onClick={handleSaveProfile}>Profil speichern</SaveButton>
                            <CancelButton onClick={handleCancelEdit}>Abbrechen</CancelButton>
                        </div>
                    </div>
                </Box>
            </ProfileContainer>
        </div>
    );
}

export default AddCompanyProfile;